import React, { ReactNode, useContext, useMemo } from "react";
import { Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BoxProps } from "@mui/material";
import { useSearchParams, useLocation } from "react-router";
import { mediaDocumentUrl } from "../../Helpers/mediaDocumentUrl/mediaDocumentUrl";
import BNBox from "../BNBox/BNBox";
import { cloudinaryUrl, Env } from "../../Helpers/cloudinaryUrl/cloudinaryUrl";
import { sxExplainer } from "../Explainer/Explainer";

export interface IRichTextContext {
  useCloudinary: boolean;
  mediaUrl: string;
  env?: Env;
  resolveUrl: (umbracoUrl: string) => string;
}

export interface BNRichTextProps {
  html?: string;
  useGrid?: boolean;
  className?: string;
  hidden?: {
    smDown: boolean;
    smUp: boolean;
  };
  sx?: BoxProps["sx"];
  dataUit?: string;
}

const defaultValues: IRichTextContext = {
  useCloudinary: true,
  mediaUrl: "https://cms.banknorwegian.com",
  env: "prod",
  resolveUrl: (value: string) => value,
};

export const RichTextContext = React.createContext<IRichTextContext>(defaultValues);

export const RichTextContextProvider: React.FC<{
  richTextContext: IRichTextContext;
  children: ReactNode;
}> = ({ richTextContext, children }) => {
  return <RichTextContext.Provider value={richTextContext}>{children}</RichTextContext.Provider>;
};

const BNRichText: React.FC<BNRichTextProps> = (props) => {
  const { useCloudinary, mediaUrl, env, resolveUrl } = useContext(RichTextContext);

  const theme = useTheme();

  // Use both hooks to ensure compatibility with SSR
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // During SSR, searchParams might not work, so we also parse from location.search
  const getQueryParam = (paramName: string): string | null => {
    // Try useSearchParams first (works in client-side and some SSR scenarios)
    for (const [key, value] of searchParams.entries()) {
      if (key.toLowerCase() === paramName.toLowerCase()) {
        return value;
      }
    }

    // Fallback: parse from location.search directly (works in SSR with StaticRouter)
    if (location.search) {
      const params = new URLSearchParams(location.search);
      for (const [key, value] of params.entries()) {
        if (key.toLowerCase() === paramName.toLowerCase()) {
          return value;
        }
      }
    }

    return null;
  };

  const { html: htmlRaw = "", useGrid, className, hidden, sx, dataUit } = props;

  const html = useMemo(() => {
    if (!htmlRaw) {
      return "";
    }

    const replaceQueryParamTemplates = (htmlInput: string) => {
      // Match href attributes that contain template placeholders like {returnUrl}, {someParam}, etc.
      const hrefTemplateRegex = /href="([^"]*\{[^}]+\}[^"]*)"/g;
      let htmlResult = htmlInput;
      const matches = Array.from(htmlInput.matchAll(hrefTemplateRegex));

      for (const match of matches) {
        const originalHref = match[1];

        // Split URL into base and query string
        const [baseUrl, queryString] = originalHref.split("?");

        if (!queryString) {
          continue; // No query string, nothing to process
        }

        // Parse query parameters
        const params = queryString.split("&");
        const processedParams: string[] = [];

        for (const param of params) {
          const [key, value] = param.split("=");

          // Check if value contains a template
          const templateMatch = value?.match(/^\{([^}]+)\}$/);

          if (templateMatch) {
            const paramName = templateMatch[1];

            // Case-insensitive lookup using our helper function
            const paramValue = getQueryParam(paramName);

            if (paramValue !== null && paramValue !== "") {
              // Replace template with actual value
              processedParams.push(`${key}=${encodeURIComponent(paramValue)}`);
            }
            // If paramValue is null or empty, skip this parameter (don't add it)
          } else {
            // Not a template, keep as-is
            processedParams.push(param);
          }
        }

        // Reconstruct the href
        const processedHref = processedParams.length > 0 ? `${baseUrl}?${processedParams.join("&")}` : baseUrl;

        // Only replace if we actually processed any templates
        if (processedHref !== originalHref) {
          htmlResult = htmlResult.replace(`href="${originalHref}"`, `href="${processedHref}"`);
        }
      }

      return htmlResult;
    };

    const htmlWithCorrectUrls = (htmlInput: string) => {
      const listOfUrlsToFix = htmlInput.matchAll(/href="(.*?)"/g);
      let htmlResult = htmlInput;
      for (const urlMatch of listOfUrlsToFix) {
        const urlToFix = urlMatch[1];
        if (urlToFix && !urlToFix.match(/^http[s]{0,1}/) && !urlToFix?.match(/^\/media\//)) {
          htmlResult = htmlResult.replace(urlToFix, resolveUrl(urlToFix));
        }
      }
      return htmlResult;
    };
    const htmlWithCorrectMediaUrls = (htmlInput: string) => {
      const listOfUrlsToFix = htmlInput.matchAll(/src="(.*?)"/g);
      let htmlResult = htmlInput;
      for (const urlMatch of listOfUrlsToFix) {
        const currentImageSrc = urlMatch[1];
        if (currentImageSrc?.match(/^\/media\//)) {
          const newImageSrc = useCloudinary ? cloudinaryUrl(currentImageSrc, env) : `${mediaUrl}${currentImageSrc}`;
          if (newImageSrc) {
            htmlResult = htmlResult.replace(currentImageSrc, newImageSrc);
          }
        }
      }
      return htmlResult;
    };

    const htmlWithCorrectMediaDocumentUrls = (htmlInput: string) => {
      const listOfUrlsToFix = htmlInput.matchAll(/href="(.*?)"/g);
      let htmlResult = htmlInput;
      for (const urlMatch of listOfUrlsToFix) {
        const currentMediaDocumentSrc = urlMatch[1];
        if (currentMediaDocumentSrc?.match(/^\/media\//)) {
          const newMediaDocumentSrc = useCloudinary
            ? mediaDocumentUrl(currentMediaDocumentSrc, env)
            : `${mediaUrl}${currentMediaDocumentSrc}`;
          if (newMediaDocumentSrc) {
            htmlResult = htmlResult.replace(currentMediaDocumentSrc, newMediaDocumentSrc);
          }
        }
      }
      return htmlResult;
    };

    // Process query parameter templates FIRST, before URL resolution
    const htmlWithTemplates = replaceQueryParamTemplates(htmlRaw);
    const htmlCorrectUrls = htmlWithCorrectUrls(htmlWithTemplates);
    return htmlWithCorrectMediaUrls(htmlWithCorrectMediaDocumentUrls(htmlCorrectUrls));
  }, [htmlRaw, useCloudinary, env, mediaUrl, resolveUrl, searchParams, location.search]);

  const sxRichText = {
    "& h2": {
      lineHeight: { xs: "3rem", md: "3.5rem" },
      fontSize: { xs: "2rem", md: "3.2rem" },
    },

    "& ul": {
      listStyleType: "disc",
      marginBottom: "1rem",
    },
    "& p": {
      marginTop: 0,
    },
    [theme.breakpoints.up("sm")]: {
      display: hidden?.smUp ? "none" : undefined,
    },
    [theme.breakpoints.down("sm")]: {
      display: hidden?.smDown ? "none" : undefined,
      [".table-responsive"]: {
        maxWidth: "calc(100vw - 11.5rem)",
      },
    },
    ...sxExplainer,
    ...sx,
  };

  return (
    <BNBox
      className={className || ""}
      dangerouslySetInnerHTML={useGrid ? undefined : { __html: html }}
      sx={sxRichText as any} // TODO Correct typing
      data-uit={useGrid ? undefined : dataUit}
    >
      {useGrid && (
        <Grid container>
          <Grid dangerouslySetInnerHTML={{ __html: html }} data-uit={dataUit} size={12} />
        </Grid>
      )}
    </BNBox>
  );
};

export default React.memo(BNRichText);
